import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound } from 'lucide-react';
import OpsModal from '@/components/ops/OpsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

export default function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    if (!current || !next || !confirm) { setError('All fields are required'); return; }
    if (next !== confirm) { setError('New passwords do not match'); return; }
    if (next.length < 8) { setError('New password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, getAuth());
      toast.success('Password updated');
      onClose();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to update password';
      setError(typeof msg === 'string' ? msg : 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <button
        onClick={onClose}
        className="h-9 text-[13px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md px-3 transition-colors"
        data-testid="cancel-password-change"
      >Cancel</button>
      <button
        onClick={submit}
        disabled={saving}
        data-testid="submit-password-change"
        className="h-9 bg-slate-900 hover:bg-slate-800 text-white px-4 rounded-md text-[13px] font-medium shadow-sm disabled:opacity-50 transition-colors"
      >
        {saving ? 'Saving…' : 'Update password'}
      </button>
    </div>
  );

  return (
    <OpsModal
      title="Change password"
      subtitle="Your password is private — only you control it."
      icon={KeyRound}
      onClose={onClose}
      size="sm"
      zClass="z-[70]"
      footer={footer}
      testid="change-password-modal"
    >
      <div className="px-5 py-4 space-y-3">
        {[
          ['Current password', current, setCurrent, 'current-password-input'],
          ['New password',     next,    setNext,    'new-password-input'],
          ['Confirm new password', confirm, setConfirm, 'confirm-password-input'],
        ].map(([label, val, setter, tid]) => (
          <div key={tid}>
            <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">{label}</label>
            <input
              type="password"
              value={val}
              onChange={e => { setter(e.target.value); setError(''); }}
              data-testid={tid}
              className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors"
            />
          </div>
        ))}
        <p className="text-[11px] text-slate-400">Minimum 8 characters.</p>
        {error && (
          <div
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700"
            data-testid="password-change-error"
          >
            {error}
          </div>
        )}
      </div>
    </OpsModal>
  );
}
